import * as React from 'react';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import { useDispatch, useSelector } from 'react-redux';
import { setPage } from '../../store/slices/pageSlice';
import { clearPromoCreationState, isPromoFormPage } from '../../utils/promoFormDraft';
import { usePromoLeaveGuard } from '../../context/PromoLeaveGuardContext';

import HomeInActive from '../icons/HomeInActive';
import HomeActive from '../icons/HomeActive';
import CrystalsInActive from '../icons/CrystalsInActive';
import CrystalsActive from '../icons/CrystalsActive';
import CCInActive from '../icons/CCInActive';
import CCActive from '../icons/CCActive';
import CryptoInActive from '../icons/CryptoInActive';
import CryptoActive from '../icons/CryptoActive';
import BalanceInActive from '../icons/BalanceInActive';
import BalanceActive from '../icons/BalanceActive';

/**
 * Liquid Glass bottom bar — максимальный стеклянный эффект без свечения
 */
export default function BottomBarGlass() {
    const dispatch = useDispatch();
    const { requestLeave } = usePromoLeaveGuard();
    const currentPageId = useSelector((state) => state.page.currentPageId);
    const isEditMode = useSelector((state) => state.selectedPromo.isEditMode);
    const bonusPosition = useSelector((state) => state.promoForm?.bonusPosition);
    const isBonusMode = !!bonusPosition;

    const navItems = [
        { id: 0, label: 'Home', icon: HomeInActive, iconActive: HomeActive },
        { id: 1, label: 'Crystals', icon: CrystalsInActive, iconActive: CrystalsActive },
        { id: 2, label: 'C & C', icon: CCInActive, iconActive: CCActive },
        { id: 3, label: 'Crypto', icon: CryptoInActive, iconActive: CryptoActive },
        { id: 6, label: 'Balance', icon: BalanceInActive, iconActive: BalanceActive },
    ];

    const activeIndex = navItems.findIndex(item => item.id === currentPageId);
    const value = activeIndex !== -1 ? activeIndex : 0;

    const navigateToPage = (selectedPage) => {
        clearPromoCreationState(dispatch);
        dispatch(setPage({ id: selectedPage.id, name: selectedPage.label }));
    };

    const handleChange = (event, newValue) => {
        const selectedPage = navItems[newValue];
        if (selectedPage.id === currentPageId) return;

        if (isPromoFormPage(currentPageId)) {
            requestLeave(() => navigateToPage(selectedPage));
            return;
        }

        navigateToPage(selectedPage);
    };

    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                padding: '0 12px 8px 12px',
                paddingBottom: 'calc(8px + var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))',
            }}
        >
            {/* Liquid glass container */}
            <BottomNavigation
                showLabels
                value={value}
                onChange={handleChange}
                sx={{
                    boxSizing: 'border-box',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 14px',
                    position: 'relative',
                    overflow: 'hidden',
                    width: '100%',
                    height: '68px',
                    // Liquid glass base
                    background: `
                        linear-gradient(
                            180deg,
                            rgba(234, 234, 234, 0.12) 0%,
                            rgba(234, 234, 234, 0.04) 50%,
                            rgba(234, 234, 234, 0.08) 100%
                        )
                    `,
                    backdropFilter: 'blur(40px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                    borderRadius: '24px',
                    // Top highlight — имитация блика на стекле
                    borderTop: '1px solid rgba(234, 234, 234, 0.35)',
                    borderLeft: '1px solid rgba(234, 234, 234, 0.18)',
                    borderRight: '1px solid rgba(234, 234, 234, 0.08)',
                    borderBottom: '1px solid rgba(234, 234, 234, 0.05)',
                    // Subtle inner shadow for depth
                    boxShadow: `
                        inset 0 1px 1px rgba(234, 234, 234, 0.15),
                        inset 0 -1px 2px rgba(0, 0, 0, 0.08),
                        0 8px 32px rgba(0, 0, 0, 0.18)
                    `,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '10%',
                        right: '10%',
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent, rgba(234, 234, 234,0.5), transparent)',
                        borderRadius: '50%',
                    },
                }}
            >
                {navItems.map((item) => {
                    const isActive = currentPageId === item.id;
                    const isActiveEdit = isActive && isEditMode;
                    const isActiveBonus = isActive && isBonusMode;
                    
                    // Определяем фон для активного элемента
                    const getActiveBackground = () => {
                        if (!isActive) return 'transparent';
                        if (isActiveBonus) {
                            return `linear-gradient(
                                90deg,
                                rgba(220, 31, 255, 0.35) 0%,
                                rgba(58, 136, 247, 0.35) 100%
                            )`;
                        }
                        if (isActiveEdit) {
                            return `linear-gradient(
                                145deg,
                                rgba(255, 200, 50, 0.25) 0%,
                                rgba(255, 180, 30, 0.12) 50%,
                                rgba(255, 200, 50, 0.18) 100%
                            )`;
                        }
                        return `linear-gradient(
                            145deg,
                            rgba(234, 234, 234, 0.18) 0%,
                            rgba(234, 234, 234, 0.06) 50%,
                            rgba(234, 234, 234, 0.12) 100%
                        )`;
                    };
                    
                    // Определяем border для активного элемента
                    const getActiveBorder = () => {
                        if (!isActive) return '1px solid transparent';
                        if (isActiveBonus) return '1px solid rgba(220, 31, 255, 0.5)';
                        if (isActiveEdit) return '1px solid rgba(255, 200, 50, 0.4)';
                        return '1px solid rgba(234, 234, 234, 0.22)';
                    };
                    
                    // Определяем цвет текста
                    const getTextColor = () => {
                        if (isActiveBonus) return '#E879F9';
                        if (isActiveEdit) return '#FFD54F';
                        if (isActive) return '#EAEAEA';
                        return 'rgba(234, 234, 234,0.5)';
                    };
                    
                    // Определяем фильтр для иконки
                    const getIconFilter = () => {
                        if (isActiveBonus) return 'sepia(1) saturate(5) hue-rotate(260deg) brightness(1.2)';
                        if (isActiveEdit) return 'sepia(1) saturate(3) hue-rotate(10deg) brightness(1.1)';
                        return 'none';
                    };
                    
                    return (
                        <BottomNavigationAction
                            key={item.id}
                            label=""
                            icon={
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        padding: isActive ? '8px 16px' : '8px 8px',
                                        borderRadius: 20,
                                        background: getActiveBackground(),
                                        border: getActiveBorder(),
                                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                        minWidth: isActive ? '72px' : '44px',
                                    }}
                                >
                                    {typeof (isActive ? item.iconActive : item.icon) === 'function' ? (
                                        React.createElement(isActive ? item.iconActive : item.icon, {
                                            width: 22,
                                            height: 23,
                                            style: {
                                                opacity: isActive ? 1 : 0.6,
                                                transition: 'opacity 0.2s ease',
                                                filter: getIconFilter(),
                                            },
                                        })
                                    ) : (
                                        <img
                                            src={isActive ? item.iconActive : item.icon}
                                            alt={item.label}
                                            style={{
                                                width: '22px',
                                                height: '22px',
                                                objectFit: 'contain',
                                                opacity: isActive ? 1 : 0.6,
                                                transition: 'opacity 0.2s ease',
                                                filter: getIconFilter(),
                                            }}
                                        />
                                    )}
                                    <span
                                        style={{
                                            fontFamily: 'Lato',
                                            fontWeight: 500,
                                            fontSize: '11px',
                                            lineHeight: '13px',
                                            textAlign: 'center',
                                            color: getTextColor(),
                                            transition: 'color 0.2s ease',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {item.label}
                                    </span>
                                </div>
                            }
                            disableRipple
                            sx={{
                                flex: isActive ? '0 0 auto' : '1 1 auto',
                                padding: 0,
                                minWidth: '44px',
                                height: '60px',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                                '& .MuiBottomNavigationAction-label': {
                                    display: 'none',
                                },
                            }}
                        />
                    );
                })}
            </BottomNavigation>
        </div>
    );
}

